import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, Download, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ApiError } from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import { pobierzPodgladObrazka, wgrajObrazekDoBiblioteki, zlecPodgladObrazka } from '@/lib/items-api';
import { nazwaZWezla } from '@/lib/nazwa-pliku-z-maszyny';

/**
 * PLIK Z KONKRETNEJ MASZYNY — pobranie, podejrzenie i ODŁOŻENIE DO BIBLIOTEKI.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ⚠️⚠️ WYDZIELONE Z `PodgladZMaszyny` 07.09.2026, bo od tego dnia to samo jest potrzebne w DWÓCH
 *  miejscach: w oknie podglądu (klik w ikonkę maszyny) i w naprawie wiersza („ściągnij i rozgłoś").
 *  Druga kopia pobierania rozjechałaby się przy pierwszej zmianie kanału, a rozjazd byłby cichy:
 *  jedno okno pokazywałoby plik, drugie „czekam" bez końca.
 *
 *  ⚠️⚠️ ZAPIS DO BIBLIOTEKI NIE WYMAGA ANI JEDNEJ NOWEJ TRASY W API, i to jest tu najważniejsze:
 *  podgląd JUŻ przynosi bajty jako `data:`-URL (ten sam kanał co zrzut ekranu), a
 *  `wgrajObrazekDoBiblioteki` przyjmuje base64. Zgłoszone 07.09.2026: „możliwość pobrania w górę
 *  z konkretnej maszyny — może być sytuacja, gdzie dla danego produktu na każdym automacie jest
 *  inny obrazek mimo tej samej nazwy".
 *
 *  ⚠️ NIE ROZSYŁAMY PO ZAPISIE (rozstrzygnięte 07.09.2026). Plik ląduje u nas, a następny krok
 *  jest osobnym kliknięciem — bo push NADPISUJE pliki na czynnych automatach, a właśnie ściągnęliśmy
 *  jedną z kilku różnych wersji i można chcieć obejrzeć pozostałe.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */

/** Jak często pytamy o wynik. Sekunda i pół to kompromis: żywe okno, a nie odpytywanie w pętli. */
const ODSTEP_MS = 1_500;

export function PodgladPliku({
  groupId,
  agentId,
  nodeId,
  nazwaPliku,
  /** Nazwy plików, które biblioteka już ma — do podpowiedzi nazwy przy kolizji. */
  wBibliotece,
  onZapisano,
}: {
  groupId: string;
  agentId: string;
  /** Krótki identyfikator węzła — wchodzi do podpowiedzianej nazwy pliku. */
  nodeId: string;
  nazwaPliku: string;
  wBibliotece?: readonly string[];
  /** Wywoływane po udanym zapisie — naprawa wiersza idzie z tego dalej. `undefined` = nie oferuj zapisu. */
  onZapisano?: (nazwaPliku: string) => void;
}) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [zadanieId, setZadanieId] = useState<string | null>(null);
  const [blad, setBlad] = useState<string | null>(null);
  /** `null` = pole zapisu jeszcze nieotwarte; napis = nazwa do zapisania. */
  const [nazwaDoZapisu, setNazwaDoZapisu] = useState<string | null>(null);
  const [zapisano, setZapisano] = useState<string | null>(null);

  const zlec = useMutation({
    mutationFn: () => zlecPodgladObrazka(groupId, agentId, nazwaPliku),
    onSuccess: (r) => setZadanieId(r.zadanieId),
    /**
     * ⚠️ POWÓD ODMOWY POKAZUJEMY W OKNIE, nie w dymku obok. Najczęstszym powodem będzie „ten agent
     * jest za stary" — a to jest zdanie, po którym wiadomo, co zrobić.
     */
    onError: (e: Error) => setBlad(e instanceof ApiError ? e.message : t('common.error')),
  });

  /** ⚠️ Zlecamy RAZ, przy otwarciu — zapadka na `zadanieId`. */
  useEffect(() => {
    if (zadanieId === null && !zlec.isPending && blad === null) zlec.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stan = useQuery({
    queryKey: ['podglad-z-maszyny', groupId, zadanieId],
    queryFn: () => pobierzPodgladObrazka(groupId, zadanieId!),
    enabled: zadanieId !== null,
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      /** ⚠️ Przestajemy pytać po KAŻDEJ odpowiedzi ostatecznej — także po nieudanej. */
      return s === undefined || s === 'QUEUED' || s === 'SENT' || s === 'RUNNING' ? ODSTEP_MS : false;
    },
  });

  const d = stan.data;
  const czekamy =
    blad === null &&
    (zadanieId === null || d === undefined || d.status === 'QUEUED' || d.status === 'SENT' || d.status === 'RUNNING');

  const zapisz = useMutation({
    /**
     * ⚠️⚠️ BAJTY BIERZEMY Z `data:`-URL-a, KTÓRY JUŻ MAMY W OKNIE. Drugie pobranie z maszyny
     * znaczyłoby drugie zadanie dla agenta i kilka minut czekania — po to, żeby dostać dokładnie
     * to, co widzimy na ekranie.
     */
    mutationFn: (nazwa: string) => {
      const url = d?.obraz ?? '';
      const base64 = url.slice(url.indexOf(',') + 1);
      return wgrajObrazekDoBiblioteki(groupId, nazwa, base64);
    },
    onSuccess: (r) => {
      setZapisano(r.nazwaPliku);
      setNazwaDoZapisu(null);
      /** ⚠️ Galeria i pokrycie muszą to zobaczyć — inaczej „portal nie ma tego pliku" zostaje na ekranie. */
      void qc.invalidateQueries({ queryKey: ['obrazki'] });
      void qc.invalidateQueries({ queryKey: ['pokrycie-obrazkow', groupId] });
      onZapisano?.(r.nazwaPliku);
    },
    onError: (e: Error) => setBlad(e instanceof ApiError ? e.message : t('common.error')),
  });

  const kolizja = nazwaDoZapisu !== null && (wBibliotece ?? []).some((n) => n.toLowerCase() === nazwaDoZapisu.toLowerCase());

  return (
    <div className="space-y-3">
      {blad !== null && (
        <p className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-xs">
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-destructive" />
          <span className="min-w-0 break-words">{blad}</span>
        </p>
      )}

      {czekamy && (
        <p className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          {t('podgladM.czekamy')}
        </p>
      )}

      {d !== undefined && d.status === 'FAILED' && (
        <p className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-xs">
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-destructive" />
          {/* ⚠️ POWÓD Z MASZYNY, nie „nie udało się": to on mówi, czy pliku nie ma, czy jest pusty. */}
          <span className="min-w-0 break-words">{d.error ?? t('podgladM.nieudane')}</span>
        </p>
      )}

      {d !== undefined && d.status === 'WYGASL' && (
        <p className="rounded-md border border-border p-3 text-xs text-muted-foreground">{t('podgladM.wygasl')}</p>
      )}

      {d !== undefined && d.obraz !== null && (
        <div className="space-y-2">
          {/*
            ⚠️ `data:`-URL, nie adres magazynu: bajty nigdzie nie leżą na stałe, a nasze CSP ma
            `img-src 'self' data:` — dokładnie ten sam kanał, którym idzie zrzut ekranu.
          */}
          <img
            src={d.obraz}
            alt={d.nazwaPliku ?? nazwaPliku}
            className="max-h-[52vh] w-full rounded-md border border-border bg-muted/30 object-contain"
          />
          <p className="text-[11px] text-muted-foreground">
            {t('podgladM.stopka')
              .replace('{plik}', d.nazwaPliku ?? nazwaPliku)
              .replace('{wezel}', d.nodeId)
              .replace('{kb}', String(Math.round((d.bajtow ?? 0) / 1024)))}
          </p>

          {/* ══════ ZAPIS DO BIBLIOTEKI ══════ */}
          {onZapisano !== undefined && zapisano === null && (
            <div className="rounded-md border border-border bg-muted/30 p-2">
              {nazwaDoZapisu === null ? (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    /**
                     * ⚠️⚠️ NAZWA PODPOWIEDZIANA Z WĘZŁEM, GDY ORYGINALNA JEST ZAJĘTA — rozstrzygnięte
                     * 07.09.2026. Po to istnieje ta funkcja: pod jedną nazwą leżą RÓŻNE obrazki na
                     * różnych automatach, więc trzeba móc ściągnąć wersję z każdego i je porównać.
                     * Nadpisanie zostaje możliwe, ale świadome — nie jest domyślne.
                     */
                    setNazwaDoZapisu(nazwaZWezla(nazwaPliku, nodeId, wBibliotece ?? []))
                  }
                >
                  <Download className="size-3.5" />
                  {t('podgladM.zapiszWBibliotece')}
                </Button>
              ) : (
                <div className="space-y-1.5">
                  <span className="block text-[11px] text-muted-foreground">{t('podgladM.nazwaWBibliotece')}</span>
                  <div className="flex flex-wrap items-center gap-2">
                    <Input
                      className="h-8 max-w-72 font-mono text-xs"
                      value={nazwaDoZapisu}
                      onChange={(e) => setNazwaDoZapisu(e.target.value)}
                      autoComplete="off"
                    />
                    <Button
                      type="button"
                      size="sm"
                      disabled={zapisz.isPending || nazwaDoZapisu.trim() === ''}
                      onClick={() => zapisz.mutate(nazwaDoZapisu.trim())}
                    >
                      {zapisz.isPending ? <Loader2 className="size-3.5 animate-spin" /> : <Download className="size-3.5" />}
                      {kolizja ? t('podgladM.zapiszNadpisz') : t('podgladM.zapisz')}
                    </Button>
                    <Button type="button" size="sm" variant="ghost" onClick={() => setNazwaDoZapisu(null)}>
                      {t('common.cancel')}
                    </Button>
                  </div>
                  {/*
                    ⚠️⚠️ OSTRZEŻENIE O NADPISANIU BIBLIOTEKI, nie automatów. Zapis tutaj nie rusza
                    ani jednej maszyny — ale zamazuje bajty, które portal miał, a to jest jedyna
                    kopia, jaką mamy poza automatami.
                  */}
                  {kolizja && (
                    <p className="rounded-md border border-[var(--warning)]/40 bg-[var(--warning)]/10 p-2 text-[11px] leading-snug">
                      {t('podgladM.nadpiszeBiblioteke').replace('{plik}', nazwaDoZapisu)}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {zapisano !== null && (
            <p className="flex items-start gap-2 rounded-md border border-[var(--success)]/40 bg-[var(--success)]/10 p-2 text-[11px] leading-snug">
              <Check className="mt-0.5 size-3.5 shrink-0 text-[var(--success)]" />
              {/*
                ⚠️ MÓWIMY, CO SIĘ NIE STAŁO: plik jest u nas i NIE poszedł na automaty. Bez tego zdania
                „zapisano" czyta się jak „rozesłano", a to dwie różne rzeczy i druga jest nieodwracalna.
              */}
              <span>{t('podgladM.zapisanoOpis').replace('{plik}', zapisano)}</span>
            </p>
          )}
        </div>
      )}

      {/* ⚠️ PONOWIENIE JEST TU POTRZEBNE: obraz wygasa po kwadransie, a zadanie bywa nieudane. */}
      {!czekamy && (
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={zlec.isPending}
          onClick={() => {
            setBlad(null);
            setZadanieId(null);
            setZapisano(null);
            zlec.mutate();
          }}
        >
          {zlec.isPending ? <Loader2 className="size-3.5 animate-spin" /> : null}
          {t('podgladM.ponow')}
        </Button>
      )}
    </div>
  );
}
